import { useEffect, useState } from "react";
import { apiQuery } from "app/game-data/useQuery";

const VIDEO_METADATA_QUERY = /* GraphQL */ `
  query GetTalkPlayCount($slug: [String!]) {
    videos(slug: $slug, first: 1, channel: ALL, isPublished: true) {
      nodes {
        id
        title
        viewedCount
        speakers {
          nodes {
            firstname
            lastname
          }
        }
      }
    }
  }
`;

interface SpeakerData {
  firstname: string;
  lastname: string;
}

interface TedTalkMetadata {
  id: number;
  title: string;
  viewedCount: number;
  speakers: {
    nodes: SpeakerData[];
  };
}

const EMPTY_METADATA: TedTalkMetadata = {
  id: 0,
  title: "",
  viewedCount: 0,
  speakers: {
    nodes: [],
  },
};

type VideoMetadataQueryResponseType = {
  videos: {
    nodes: TedTalkMetadata[];
  };
};

export function useFetchTedTalkMetadata(slug: string) {
  const [loading, setLoading] = useState(true);
  const [metadata, setMetadata] = useState<TedTalkMetadata>(EMPTY_METADATA);

  const apiUrl = import.meta.env.DEV ? "/ted-api" : "https://graphql.ted.com";

  useEffect(() => {
    if (!slug) {
      return;
    }

    async function fetchTalkMetadata(slug: string) {
      try {
        setLoading(true);
        const { data, error } = await apiQuery<
          VideoMetadataQueryResponseType,
          { slug: string[] }
        >(
          VIDEO_METADATA_QUERY,
          {
            slug: [slug],
          },
          apiUrl,
        );
        if (!error && data?.videos.nodes && data.videos.nodes.length > 0) {
          setMetadata(data.videos.nodes[0]);
        }
      } catch (err: unknown) {
        if (err instanceof Error) console.error(err.message);
        else console.error("Unknown error");
      } finally {
        setLoading(false);
      }
    }

    fetchTalkMetadata(slug);
  }, [slug]);

  return {
    loading,
    speaker: concatenateSpeakerName(metadata),
    title: metadata.title,
    longTitle: concatenateTitle(metadata),
    viewedCount: metadata.viewedCount,
  };
}

function concatenateTitle(metadata: TedTalkMetadata): string {
  if (metadata == null) {
    return "";
  }

  const title = metadata.title;
  return `${concatenateSpeakerName(metadata)}: ${title}`;
}

function concatenateSpeakerName(metadata: TedTalkMetadata): string {
  if (
    metadata == null ||
    metadata.speakers == null ||
    metadata.speakers.nodes.length === 0
  ) {
    return "";
  }

  const firstName = metadata.speakers.nodes[0].firstname;
  const lastName = metadata.speakers.nodes[0].lastname;
  return `${firstName} ${lastName}`;
}
